import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Clipboard,
  ExternalLink,
  Globe,
  Link as LinkIcon,
  Mail,
  Phone,
  User as UserIcon,
} from 'lucide-react';
import {
  formatDuration,
  formatRelativeDate,
  type Client,
  type PaginatedResponse,
  type Task,
  type User,
  type Website,
} from '@oneup/shared';
import {
  clientsApi,
  communicationsApi,
  tasksApi,
  usersApi,
  websitesApi,
  type CommunicationTimelineItem,
} from '@/lib/api';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { MarkdownNotes } from '@/components/markdown-notes';
import { RecordDetailHeading, RecordDetailShell } from '@/components/workspace';
import { pushRecent } from '@/lib/recents';
import { useUiStore } from '@/stores/ui';
import { cn } from '@/lib/utils';
import { taskStatusVariant } from '@/lib/task-status';
import { CommunicationDetailsDrawer } from './CommunicationsView';

interface DrawerProps {
  onClose: () => void;
  onOpenTask?: (id: string) => void;
  onOpenClient?: (id: string) => void;
  onOpenWebsite?: (id: string) => void;
  onOpenUser?: (id: string) => void;
}

export function ClientDrawer({
  clientId,
  onClose,
  onOpenTask,
  onOpenClient,
  onOpenWebsite,
  onOpenUser,
}: DrawerProps & { clientId: string }) {
  const query = useQuery({
    queryKey: ['clients', 'detail', clientId],
    queryFn: () => clientsApi.get(clientId),
  });

  useEffect(() => {
    if (!query.data) return;
    pushRecent({
      type: 'client',
      id: query.data.id,
      name: query.data.name,
      secondary: query.data.status,
    });
  }, [query.data]);

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] md:max-w-[720px]">
        {query.data ? (
          <ClientDetailView
            client={query.data}
            onOpenTask={onOpenTask}
            onOpenClient={onOpenClient}
            onOpenWebsite={onOpenWebsite}
            onOpenUser={onOpenUser}
          />
        ) : (
          <DrawerLoading label="client" error={query.error} />
        )}
      </SheetContent>
    </Sheet>
  );
}

export function ClientDetailWorkspace({
  clientId,
  onClose,
  listTitle,
  selectedIndex,
  totalRecords,
  onPrevious,
  onNext,
  onOpenTask,
  onOpenClient,
  onOpenWebsite,
  onOpenUser,
}: DrawerProps & {
  clientId: string;
  listTitle?: string;
  selectedIndex?: number;
  totalRecords?: number;
  onPrevious?: () => void;
  onNext?: () => void;
}) {
  const query = useQuery({
    queryKey: ['clients', 'detail', clientId],
    queryFn: () => clientsApi.get(clientId),
  });

  useEffect(() => {
    if (!query.data) return;
    pushRecent({
      type: 'client',
      id: query.data.id,
      name: query.data.name,
      secondary: query.data.status,
    });
  }, [query.data]);

  if (!query.data) {
    return (
      <section className="min-h-[560px] overflow-hidden rounded-lg border border-border bg-card">
        <DrawerLoading label="client" error={query.error} />
      </section>
    );
  }

  return (
    <ClientDetailView
      client={query.data}
      surface="workspace"
      onClose={onClose}
      listTitle={listTitle}
      selectedIndex={selectedIndex}
      totalRecords={totalRecords}
      onPrevious={onPrevious}
      onNext={onNext}
      onOpenTask={onOpenTask}
      onOpenClient={onOpenClient}
      onOpenWebsite={onOpenWebsite}
      onOpenUser={onOpenUser}
    />
  );
}

function ClientDetailView({
  client,
  surface = 'drawer',
  onClose,
  listTitle,
  selectedIndex,
  totalRecords,
  onPrevious,
  onNext,
  onOpenTask,
  onOpenClient,
  onOpenWebsite,
  onOpenUser,
}: {
  client: Client;
  surface?: 'drawer' | 'workspace';
  onClose?: () => void;
  listTitle?: string;
  selectedIndex?: number;
  totalRecords?: number;
  onPrevious?: () => void;
  onNext?: () => void;
  onOpenTask?: (id: string) => void;
  onOpenClient?: (id: string) => void;
  onOpenWebsite?: (id: string) => void;
  onOpenUser?: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCommunication, setSelectedCommunication] =
    useState<CommunicationTimelineItem | null>(null);
  const [draft, setDraft] = useState({
    name: client.name,
    status: client.status ?? '',
    website: client.website ?? '',
    notes: client.notes ?? '',
  });
  useEffect(() => {
    setDraft({
      name: client.name,
      status: client.status ?? '',
      website: client.website ?? '',
      notes: client.notes ?? '',
    });
  }, [client.id, client.name, client.status, client.website, client.notes]);
  const saveMutation = useMutation({
    mutationFn: () => clientsApi.update(client.id, draft),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setNotice('Organization updated');
      setIsEditing(false);
    },
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : 'Save failed', 'destructive'),
  });
  const deleteMutation = useMutation({
    mutationFn: () => clientsApi.delete(client.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setNotice('Organization archived');
    },
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : 'Delete failed', 'destructive'),
  });
  const tasksQuery = useQuery({
    queryKey: ['tasks', { clientId: client.id }],
    queryFn: () => tasksApi.list({ clientId: client.id, limit: 300 }),
    enabled: Boolean(client.id),
  });
  const websitesQuery = useQuery({
    queryKey: ['websites', { clientId: client.id }],
    queryFn: () => websitesApi.list({ clientId: client.id }),
    enabled: Boolean(client.id),
  });
  const usersQuery = useQuery({
    queryKey: ['users', { clientId: client.id }],
    queryFn: () => usersApi.list({ clientId: client.id }),
    enabled: Boolean(client.id),
  });
  const communicationsQuery = useQuery({
    queryKey: ['communications', { clientId: client.id }],
    queryFn: () => communicationsApi.list({ clientId: client.id, limit: 50 }),
    enabled: Boolean(client.id),
    staleTime: 60_000,
  });

  const content = (
    <div
      className={cn(
        'text-[13px]',
        surface === 'drawer' ? 'flex-1 overflow-y-auto px-5 py-4' : 'px-4 py-4',
      )}
    >
      <Tabs defaultValue="overview" className="flex flex-col gap-3">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            <TabsTrigger value="communications">Communications</TabsTrigger>
            <TabsTrigger value="linked">Linked</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {isEditing && (
              <Card className="space-y-2 px-4 py-3">
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  placeholder="Name"
                />
                <Input
                  value={draft.status}
                  onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                  placeholder="Status"
                />
                <Input
                  value={draft.website}
                  onChange={(e) => setDraft((d) => ({ ...d, website: e.target.value }))}
                  placeholder="Website"
                />
                <Input
                  value={draft.notes}
                  onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                  placeholder="Notes"
                />
              </Card>
            )}
            <div className="grid grid-cols-3 gap-3">
              <SummaryStat label="Open Support" value={String(client.openSupportCount ?? 0)} />
              <SummaryStat label="Billable" value={formatDuration(client.totalMinutesBillable)} />
              <SummaryStat label="Tracked" value={formatDuration(client.totalMinutesTracked)} />
            </div>
            <Card className="space-y-2 px-4 py-3">
              <InfoRow label="Hourly Rate" value={formatCurrency(client.hourlyRate, '/hr')} />
              <InfoRow
                label="Stripe Customer"
                value={client.stripeCustomerId}
                copyValue={client.stripeCustomerId}
              />
              <InfoRow label="Time Token Rate" value={formatNumber(client.timeTokenRate)} />
              <InfoRow label="Website" value={client.website} href={client.website} />
              {client.parentOrganizationId && (
                <InfoRow
                  label="Parent Organization"
                  value={client.parentOrganizationName ?? client.parentOrganizationId}
                  onClick={() => onOpenClient?.(client.parentOrganizationId!)}
                />
              )}
            </Card>
            <div>
              <SectionHeading>Notes</SectionHeading>
              <Card className="px-4 py-3">
                <MarkdownNotes notes={client.notes} emptyState="No client notes." />
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="tasks">
            <TaskList tasks={tasksQuery.data?.items ?? []} onOpenTask={onOpenTask} />
          </TabsContent>

          <TabsContent value="communications">
            <CommunicationList
              items={communicationsQuery.data?.items ?? []}
              isLoading={communicationsQuery.isLoading}
              error={communicationsQuery.error}
              onOpen={setSelectedCommunication}
            />
          </TabsContent>

          <TabsContent value="linked" className="space-y-4">
            <div>
              <SectionHeading>Websites</SectionHeading>
              <WebsiteList
                websites={websitesQuery.data?.items ?? []}
                onOpenWebsite={onOpenWebsite}
              />
            </div>
            <div>
              <SectionHeading>Contacts</SectionHeading>
              <UserList users={usersQuery.data?.items ?? []} onOpenUser={onOpenUser} />
            </div>
          </TabsContent>
        </Tabs>
      </div>
  );
  const commDrawer = selectedCommunication ? (
    <CommunicationDetailsDrawer
      item={selectedCommunication}
      onClose={() => setSelectedCommunication(null)}
    />
  ) : null;

  if (surface === 'workspace') {
    return (
      <RecordDetailShell
        title={client.name}
        recordType={listTitle}
        onClose={onClose ?? (() => undefined)}
        canGoPrevious={typeof selectedIndex === 'number' && selectedIndex > 0}
        canGoNext={
          typeof selectedIndex === 'number' &&
          typeof totalRecords === 'number' &&
          selectedIndex >= 0 &&
          selectedIndex < totalRecords - 1
        }
        onPrevious={onPrevious}
        onNext={onNext}
        isEditing={isEditing}
        onEdit={() => setIsEditing((v) => !v)}
        onSave={() => saveMutation.mutate()}
        savePending={saveMutation.isPending}
        onDelete={() => deleteMutation.mutate()}
        deletePending={deleteMutation.isPending}
        className="min-h-[560px]"
      >
        <RecordDetailHeading
          media={<Avatar name={client.name} imageUrl={client.logoUrl} icon={Building2} />}
          title={client.name}
          badges={
            <>
              {client.status && <Badge variant="muted">{client.status}</Badge>}
              {client.relationshipType && <span>{client.relationshipType}</span>}
              {client.lastModifiedAt && (
                <span>Updated {formatRelativeDate(client.lastModifiedAt)}</span>
              )}
            </>
          }
        />
        {content}
        {commDrawer}
      </RecordDetailShell>
    );
  }

  return (
    <>
      <SheetHeader>
        <div className="flex items-start gap-3 pr-6">
          <Avatar name={client.name} imageUrl={client.logoUrl} icon={Building2} />
          <div className="min-w-0 flex-1">
            <SheetTitle className="break-words">{client.name}</SheetTitle>
            <SheetDescription className="mt-1 flex flex-wrap items-center gap-2">
              {client.status && <Badge variant="muted">{client.status}</Badge>}
              {client.relationshipType && <span>{client.relationshipType}</span>}
              {client.lastModifiedAt && (
                <span>Updated {formatRelativeDate(client.lastModifiedAt)}</span>
              )}
            </SheetDescription>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setIsEditing((v) => !v)}>
              {isEditing ? 'Cancel' : 'Edit'}
            </Button>
            {isEditing && (
              <Button
                size="sm"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                Save
              </Button>
            )}
            <Button
              size="sm"
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </div>
        </div>
      </SheetHeader>
      {content}
      {commDrawer}
    </>
  );
}

export function UserDrawer({
  userId,
  onClose,
  onOpenTask,
  onOpenClient,
}: DrawerProps & { userId: string }) {
  const query = useQuery({
    queryKey: ['users', 'detail', userId],
    queryFn: () => usersApi.get(userId),
  });

  useEffect(() => {
    if (!query.data) return;
    pushRecent({
      type: 'user',
      id: query.data.id,
      name: query.data.fullName,
      secondary: query.data.email,
    });
  }, [query.data]);

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] md:max-w-[720px]">
        {query.data ? (
          <UserDrawerBody user={query.data} onOpenTask={onOpenTask} onOpenClient={onOpenClient} />
        ) : (
          <DrawerLoading label="user" error={query.error} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function UserDrawerBody({
  user,
  onOpenTask,
  onOpenClient,
}: {
  user: User;
  onOpenTask?: (id: string) => void;
  onOpenClient?: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    email: user.email ?? '',
    phone: user.phone ?? '',
    title: user.title ?? '',
    status: user.status ?? '',
  });
  useEffect(() => {
    setDraft({
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email ?? '',
      phone: user.phone ?? '',
      title: user.title ?? '',
      status: user.status ?? '',
    });
  }, [user.id, user.firstName, user.lastName, user.email, user.phone, user.title, user.status]);
  const saveMutation = useMutation({
    mutationFn: () => usersApi.update(user.id, draft),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setNotice('Contact updated');
      setIsEditing(false);
    },
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : 'Save failed', 'destructive'),
  });
  const deleteMutation = useMutation({
    mutationFn: () => usersApi.delete(user.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setNotice('Contact archived');
    },
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : 'Delete failed', 'destructive'),
  });
  const clientsQuery = useQuery({
    queryKey: ['clients', 'all'],
    queryFn: () => clientsApi.list(),
  });
  const tasksQuery = useQuery({
    queryKey: ['tasks', { assigneeEmail: user.email }],
    queryFn: () => tasksApi.list({ assigneeEmail: user.email, limit: 300 }),
    enabled: Boolean(user.email),
  });
  const clientMap = useClientMap(clientsQuery.data?.items);

  return (
    <>
      <SheetHeader>
        <div className="flex items-start gap-3 pr-6">
          <Avatar name={user.fullName} imageUrl={user.profileImageUrl} icon={UserIcon} />
          <div className="min-w-0 flex-1">
            <SheetTitle className="break-words">{user.fullName}</SheetTitle>
            <SheetDescription className="mt-1 flex flex-wrap items-center gap-2">
              {user.title && <span>{user.title}</span>}
              {user.status && <Badge variant="muted">{user.status}</Badge>}
              {user.userTypes?.map((type) => (
                <Badge key={type} variant="outline">
                  {type}
                </Badge>
              ))}
            </SheetDescription>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setIsEditing((v) => !v)}>
              {isEditing ? 'Cancel' : 'Edit'}
            </Button>
            {isEditing && (
              <Button
                size="sm"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                Save
              </Button>
            )}
            <Button
              size="sm"
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </div>
        </div>
      </SheetHeader>
      <div className="flex-1 overflow-y-auto px-5 py-4 text-[13px]">
        <Tabs defaultValue="overview" className="flex flex-col gap-3">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="linked">Linked</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {isEditing && (
              <Card className="space-y-2 px-4 py-3">
                <Input
                  value={draft.firstName}
                  onChange={(e) => setDraft((d) => ({ ...d, firstName: e.target.value }))}
                  placeholder="First name"
                />
                <Input
                  value={draft.lastName}
                  onChange={(e) => setDraft((d) => ({ ...d, lastName: e.target.value }))}
                  placeholder="Last name"
                />
                <Input
                  value={draft.email}
                  onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                  placeholder="Email"
                />
                <Input
                  value={draft.phone}
                  onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
                  placeholder="Phone"
                />
                <Input
                  value={draft.title}
                  onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                  placeholder="Title"
                />
                <Input
                  value={draft.status}
                  onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                  placeholder="Status"
                />
              </Card>
            )}
            <Card className="space-y-2 px-4 py-3">
              <InfoRow
                label="Email"
                value={user.email}
                href={user.email ? `mailto:${user.email}` : undefined}
                copyValue={user.email}
              />
              <InfoRow
                label="Phone"
                value={user.phone}
                href={user.phone ? `tel:${user.phone}` : undefined}
                copyValue={user.phone}
              />
              <InfoRow label="Last Seen" value={formatRelativeDate(user.lastSeenAt)} />
              <InfoRow label="Created" value={formatRelativeDate(user.createdAt)} />
              <InfoRow label="Last Modified" value={formatRelativeDate(user.lastModifiedAt)} />
              <InfoRow label="Agent License" value={user.agentLicense} />
            </Card>
          </TabsContent>

          <TabsContent value="linked" className="space-y-4">
            <div>
              <SectionHeading>Organizations</SectionHeading>
              <div className="flex flex-wrap gap-1.5">
                {(user.clientIds ?? []).map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onOpenClient?.(id)}
                    className="rounded-full border border-border bg-card px-2.5 py-1 text-[12px] font-medium text-foreground hover:border-primary/40 hover:text-primary"
                  >
                    {clientMap.get(id)?.name ?? id}
                  </button>
                ))}
                {(user.clientIds?.length ?? 0) === 0 && (
                  <span className="text-muted-foreground">No linked organizations.</span>
                )}
              </div>
            </div>
            <div>
              <SectionHeading>Assigned Tasks</SectionHeading>
              <TaskList tasks={tasksQuery.data?.items ?? []} onOpenTask={onOpenTask} />
            </div>
            <Card className="space-y-2 px-4 py-3">
              <InfoRow
                label="Webmaster Orgs"
                value={(user.organizationWebmasterLinkIds ?? []).join(', ') || undefined}
              />
              <InfoRow
                label="Shared Drive"
                value={user.sharedDriveFolderId}
                copyValue={user.sharedDriveFolderId}
              />
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

export function WebsiteDrawer({
  websiteId,
  onClose,
  onOpenTask,
  onOpenClient,
}: DrawerProps & { websiteId: string }) {
  const query = useQuery({
    queryKey: ['websites', 'detail', websiteId],
    queryFn: () => websitesApi.get(websiteId),
  });

  useEffect(() => {
    if (!query.data) return;
    pushRecent({
      type: 'website',
      id: query.data.id,
      name: query.data.friendlyName ?? query.data.url,
      secondary: query.data.organizationName ?? query.data.url,
    });
  }, [query.data]);

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] md:max-w-[720px]">
        {query.data ? (
          <WebsiteDrawerBody
            website={query.data}
            onOpenTask={onOpenTask}
            onOpenClient={onOpenClient}
          />
        ) : (
          <DrawerLoading label="website" error={query.error} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function WebsiteDrawerBody({
  website,
  onOpenTask,
  onOpenClient,
}: {
  website: Website;
  onOpenTask?: (id: string) => void;
  onOpenClient?: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({
    friendlyName: website.friendlyName ?? '',
    url: website.url,
    status: website.status ?? '',
  });
  useEffect(() => {
    setDraft({
      friendlyName: website.friendlyName ?? '',
      url: website.url,
      status: website.status ?? '',
    });
  }, [website.id, website.friendlyName, website.url, website.status]);
  const saveMutation = useMutation({
    mutationFn: () => websitesApi.update(website.id, draft),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websites'] });
      setNotice('Website updated');
      setIsEditing(false);
    },
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : 'Save failed', 'destructive'),
  });
  const deleteMutation = useMutation({
    mutationFn: () => websitesApi.delete(website.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websites'] });
      setNotice('Website archived');
    },
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : 'Delete failed', 'destructive'),
  });
  const tasksQuery = useQuery({
    queryKey: ['tasks', { websiteId: website.id }],
    queryFn: () => tasksApi.list({ websiteId: website.id, limit: 300 }),
    enabled: Boolean(website.id),
  });

  return (
    <>
      <SheetHeader>
        <div className="flex items-start gap-3 pr-6">
          <Avatar
            name={website.friendlyName ?? website.url}
            imageUrl={website.logoUrl}
            icon={Globe}
          />
          <div className="min-w-0 flex-1">
            <SheetTitle className="break-words">{website.friendlyName ?? website.url}</SheetTitle>
            <SheetDescription className="mt-1 flex flex-wrap items-center gap-2">
              {website.status && <Badge variant="muted">{website.status}</Badge>}
              {website.organizationName && <span>{website.organizationName}</span>}
            </SheetDescription>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setIsEditing((v) => !v)}>
              {isEditing ? 'Cancel' : 'Edit'}
            </Button>
            {isEditing && (
              <Button
                size="sm"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                Save
              </Button>
            )}
            <Button
              size="sm"
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </div>
          {website.url && (
            <Button asChild size="sm" className="h-8 gap-1.5">
              <a href={website.url} target="_blank" rel="noopener noreferrer">
                Open site
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            </Button>
          )}
        </div>
      </SheetHeader>
      <div className="flex-1 overflow-y-auto px-5 py-4 text-[13px]">
        <Tabs defaultValue="overview" className="flex flex-col gap-3">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {isEditing && (
              <Card className="space-y-2 px-4 py-3">
                <Input
                  value={draft.friendlyName}
                  onChange={(e) => setDraft((d) => ({ ...d, friendlyName: e.target.value }))}
                  placeholder="Friendly name"
                />
                <Input
                  value={draft.url}
                  onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
                  placeholder="URL"
                />
                <Input
                  value={draft.status}
                  onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                  placeholder="Status"
                />
              </Card>
            )}
            {website.screenshotUrl && (
              <img
                src={website.screenshotUrl}
                alt=""
                className="max-h-[260px] w-full rounded-md border border-border object-cover object-top"
              />
            )}
            <Card className="space-y-2 px-4 py-3">
              <InfoRow label="URL" value={website.url} href={website.url} />
              <InfoRow label="Status" value={website.status} />
              <InfoRow label="Type" value={website.type} />
              <InfoRow label="Hosting Platform" value={website.hostingPlatform} />
              <InfoRow label="Who Hosts" value={website.hostedBy} />
              <InfoRow label="Hosting Price" value={formatCurrency(website.hostingPrice)} />
              <InfoRow
                label="Subscription"
                value={formatLinkedCount(website.subscriptionIds, 'subscription')}
                copyValue={website.subscriptionIds?.join(', ')}
              />
              <InfoRow
                label="WP Admin"
                value={website.wpAdminUrl}
                href={website.wpAdminUrl}
                copyValue={website.wpAdminUrl}
              />
              {website.organizationId && (
                <InfoRow
                  label="Organization"
                  value={website.organizationName ?? website.organizationId}
                  onClick={() => onOpenClient?.(website.organizationId!)}
                />
              )}
            </Card>
          </TabsContent>

          <TabsContent value="notes">
            <Card className="px-4 py-3">
              <MarkdownNotes notes={website.notes} emptyState="No website notes." />
            </Card>
          </TabsContent>

          <TabsContent value="tasks">
            <TaskList tasks={tasksQuery.data?.items ?? []} onOpenTask={onOpenTask} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function TaskList({ tasks, onOpenTask }: { tasks: Task[]; onOpenTask?: (id: string) => void }) {
  const navigate = useNavigate();
  const grouped = useMemo(() => groupTasks(tasks), [tasks]);
  if (tasks.length === 0) {
    return <Card className="px-4 py-3 text-[12px] text-muted-foreground">No linked tasks.</Card>;
  }
  return (
    <div className="space-y-3">
      {grouped.map(([status, items]) => (
        <div key={status}>
          <SectionHeading>{status.replaceAll('_', ' ')}</SectionHeading>
          <Card>
            <ul className="divide-y divide-border">
              {items.map((task) => (
                <li key={task.id}>
                  <button
                    type="button"
                    onClick={() =>
                      onOpenTask ? onOpenTask(task.id) : navigate(`/tasks/${task.id}`)
                    }
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-[13px] hover:bg-muted/40"
                  >
                    <span className="min-w-0 truncate font-medium">{task.name}</span>
                    {task.status && (
                      <Badge variant={taskStatusVariant(task.semanticStatus)}>{task.status}</Badge>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      ))}
    </div>
  );
}

function CommunicationList({
  items,
  isLoading,
  error,
  onOpen,
}: {
  items: CommunicationTimelineItem[];
  isLoading: boolean;
  error: unknown;
  onOpen: (item: CommunicationTimelineItem) => void;
}) {
  if (isLoading) {
    return <Card className="h-32 animate-pulse" />;
  }
  if (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    return (
      <Card className="px-4 py-3 text-[12px] text-destructive">
        Could not load communications: {message}
      </Card>
    );
  }
  if (items.length === 0) {
    return (
      <Card className="px-4 py-3 text-[12px] text-muted-foreground">No linked communications.</Card>
    );
  }

  return (
    <Card>
      <ul className="divide-y divide-border">
        {items.map((item) => {
          const summary = item.summary ? communicationSummaryPreview(item.summary) : undefined;
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onOpen(item)}
                className="flex w-full gap-3 px-3 py-2.5 text-left hover:bg-muted/40"
              >
                <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border bg-muted/40">
                  <Mail className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium">
                      {item.title}
                    </span>
                    <Badge variant={item.source === 'gmail' ? 'success' : 'muted'}>
                      {item.sourceLabel}
                    </Badge>
                  </span>
                  {summary && (
                    <span className="mt-1 line-clamp-2 block text-[12px] leading-relaxed text-muted-foreground">
                      {summary}
                    </span>
                  )}
                  <span className="mt-1 block text-[11px] text-muted-foreground">
                    {formatRelativeDate(item.occurredAt)}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function WebsiteList({
  websites,
  onOpenWebsite,
}: {
  websites: Website[];
  onOpenWebsite?: (id: string) => void;
}) {
  if (websites.length === 0) {
    return <Card className="px-4 py-3 text-[12px] text-muted-foreground">No linked websites.</Card>;
  }
  return (
    <Card>
      <ul className="divide-y divide-border">
        {websites.map((website) => (
          <li key={website.id}>
            <button
              type="button"
              onClick={() => onOpenWebsite?.(website.id)}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-[13px] hover:bg-muted/40"
            >
              <span className="min-w-0 truncate font-medium">
                {website.friendlyName ?? website.url}
              </span>
              {website.status && <Badge variant="muted">{website.status}</Badge>}
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function UserList({ users, onOpenUser }: { users: User[]; onOpenUser?: (id: string) => void }) {
  if (users.length === 0) {
    return <Card className="px-4 py-3 text-[12px] text-muted-foreground">No linked contacts.</Card>;
  }
  return (
    <Card>
      <ul className="divide-y divide-border">
        {users.map((user) => (
          <li key={user.id}>
            <button
              type="button"
              onClick={() => onOpenUser?.(user.id)}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-[13px] hover:bg-muted/40"
            >
              <span className="min-w-0 truncate font-medium">{user.fullName}</span>
              {user.email && <span className="truncate text-muted-foreground">{user.email}</span>}
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function DrawerLoading({ label, error }: { label: string; error: unknown }) {
  if (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    return (
      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-destructive">
        Could not load {label}: {message}
      </div>
    );
  }
  return (
    <div className="space-y-4 p-5">
      <div className="h-5 w-2/3 animate-pulse rounded bg-muted" />
      <div className="h-28 animate-pulse rounded bg-muted/50" />
      <div className="h-48 animate-pulse rounded bg-muted/40" />
    </div>
  );
}

function Avatar({
  name,
  imageUrl,
  icon: Icon,
}: {
  name: string;
  imageUrl?: string;
  icon: typeof Building2;
}) {
  if (imageUrl) {
    return <img src={imageUrl} alt="" className="h-10 w-10 rounded-md object-cover" />;
  }
  return (
    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-[12px] font-semibold text-primary">
      {initials(name) || <Icon className="h-4 w-4" aria-hidden />}
    </div>
  );
}

function InfoRow({
  label,
  value,
  href,
  copyValue,
  onClick,
}: {
  label: string;
  value?: string;
  href?: string;
  copyValue?: string;
  onClick?: () => void;
}) {
  const setNotice = useUiStore((state) => state.setNotice);
  const display = value || 'No value';
  return (
    <div className="flex items-center gap-3">
      <span className="w-32 flex-shrink-0 text-[10px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
        {label}
      </span>
      <span className={cn('min-w-0 flex-1 truncate', !value && 'text-muted-foreground')}>
        {href && value ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex max-w-full items-center gap-1 truncate text-primary hover:underline"
          >
            <span className="truncate">{value}</span>
            <ExternalLink className="h-3 w-3 flex-shrink-0" aria-hidden />
          </a>
        ) : onClick && value ? (
          <button
            type="button"
            onClick={onClick}
            className="inline-flex max-w-full items-center gap-1 truncate text-primary hover:underline"
          >
            <span className="truncate">{value}</span>
            <LinkIcon className="h-3 w-3 flex-shrink-0" aria-hidden />
          </button>
        ) : (
          display
        )}
      </span>
      {copyValue && (
        <button
          type="button"
          aria-label={`Copy ${label}`}
          onClick={() => {
            navigator.clipboard.writeText(copyValue).then(
              () => setNotice(`${label} copied`),
              () => setNotice(`Could not copy ${label}`, 'destructive'),
            );
          }}
          className="rounded-sm p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Clipboard className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-[18px] font-bold leading-none tabular-nums">{value}</div>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.7px] text-muted-foreground">
      {children}
    </div>
  );
}

function useClientMap(clients: Client[] | undefined) {
  return useMemo(() => new Map((clients ?? []).map((client) => [client.id, client])), [clients]);
}

function groupTasks(tasks: Task[]): Array<[string, Task[]]> {
  const groups = new Map<string, Task[]>();
  tasks.forEach((task) => {
    const key = task.semanticStatus ?? 'unknown';
    groups.set(key, [...(groups.get(key) ?? []), task]);
  });
  return Array.from(groups.entries());
}

function communicationSummaryPreview(value: string): string {
  return value
    .replace(/^#+\s+/gm, '')
    .replace(/^- Gmail thread:.*$/gm, '')
    .replace(/^- Messages:.*$/gm, '')
    .replace(/^- Last message:.*$/gm, '')
    .replace(/^- Participants:.*$/gm, '')
    .replace(/^>\s*/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function formatCurrency(value: number | undefined, suffix = '') {
  if (value === undefined) return 'No value';
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}${suffix}`;
}

function formatNumber(value: number | undefined) {
  if (value === undefined) return 'No value';
  return value.toLocaleString();
}

function formatLinkedCount(ids: string[] | undefined, singular: string) {
  if (!ids || ids.length === 0) return undefined;
  if (ids.length === 1) return ids[0];
  return `${ids.length} linked ${singular}s`;
}
